import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  type EpocusEncounterQueryConstructor,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import { useUser } from "$global-providers/user-provider/provider";
import { Box, styled } from "@material-ui/core";
import { Outlet } from "react-router-dom";
import { useRxData } from "rxdb-hooks";
import { useMyIncompleteBillingStatuses } from "./_lib/billing/use-my-incomplete-billing-statuses";
import { EpocusDemoViewerSwitcher } from "./_lib/demo-viewer-switcher";
import { EpocusBottomNav } from "./_lib/epocus-bottom-nav";
import { EpocusHeader } from "./_lib/epocus-header";
import { EPOCUS_BG } from "./_lib/epocus-palette";
import { EpocusSidebar } from "./_lib/epocus-sidebar";
import { EpocusThemeProvider, epocusTheme } from "./_lib/epocus-theme-provider";
import { ProbeLinkRequiredDialog } from "./_lib/probe-link-required-dialog";
import { useCan } from "./_lib/rbac/use-can";
import { useReferralBadgeCount } from "./referrals/_lib/use-referral-badge-count";

const Shell = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100dvh",
  backgroundColor: EPOCUS_BG,
  // Fallback for browsers without dvh support
  "@supports not (height: 100dvh)": {
    height: "100vh",
  },
  // Sidebar layout on tablet+
  [epocusTheme.breakpoints.up("sm")]: {
    flexDirection: "row",
  },
});

const Main = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
  // Without min-height: 0, flex items default to min-height: auto and can grow
  // beyond Shell's 100dvh, pushing the bottom nav below the visible viewport.
  minHeight: 0,
});

const ContentArea = styled(Box)({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
});

// Query that returns all pending_review encounters; count is derived from the array length
const pendingReviewQuery: EpocusEncounterQueryConstructor = (col) =>
  col?.find({ selector: { status: { $eq: "pending_review" } } });

// Disabled variant for users without viewReviewQueue — undefined keeps the query from firing
const disabledPendingReviewQuery: EpocusEncounterQueryConstructor = () =>
  undefined;

const EpocusShell: FCwC = ({ children = <Outlet /> }) => {
  const canViewReviewQueue = useCan("viewReviewQueue");
  const { id: userId } = useUser();

  const { result: pendingEncounters } = useRxData(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    canViewReviewQueue ? pendingReviewQuery : disabledPendingReviewQuery,
  );
  const reviewCount = (pendingEncounters ?? []).filter(
    ({ performerId }) => performerId !== userId,
  ).length;
  const canViewReferrals = useCan("viewReferrals");
  const [referralsEnabled] = useFeatureFlag(featureFlags.epocus.referrals);
  // Coerce to a real boolean: an absent flag reads as `undefined`, and
  // useReferralBadgeCount treats `undefined` as enabled (its default).
  const referralCount = useReferralBadgeCount(
    Boolean(canViewReferrals && referralsEnabled),
  );
  const incompleteCount = useMyIncompleteBillingStatuses().length;

  return (
    <Shell>
      <EpocusSidebar
        reviewCount={reviewCount}
        referralCount={referralCount}
        incompleteCount={incompleteCount}
      />
      <Main>
        <EpocusHeader />
        <ContentArea>{children}</ContentArea>
        <EpocusBottomNav
          reviewCount={reviewCount}
          referralCount={referralCount}
          incompleteCount={incompleteCount}
        />
      </Main>
      <EpocusDemoViewerSwitcher />
      <ProbeLinkRequiredDialog />
    </Shell>
  );
};

export const EpocusLayout: FCwC = ({ children = <Outlet /> }) => (
  <EpocusThemeProvider>
    <EpocusShell>{children}</EpocusShell>
  </EpocusThemeProvider>
);

/** Layout without EpocusDbProvider — encounter pages use the global RxDBProvider. */
export const EpocusEncounterLayout: FCwC = ({ children = <Outlet /> }) => (
  <EpocusThemeProvider>
    <EpocusShell>{children}</EpocusShell>
  </EpocusThemeProvider>
);
